import { usePreferredDark } from '@vueuse/core';
import ColorHash, { type ColorHashOptions } from 'color-hash';
import { computed, ref, unref, type CSSProperties, type MaybeRef } from 'vue';

interface UseHashColorOptions {
  light: ColorHashOptions;
  dark: ColorHashOptions;
  additionalClasses?: string[];
}

export const HashedColorThemeAvatar = {
  light: { lightness: 0.67, saturation: 0.5 },
  dark: { lightness: 0.3, saturation: 0.5 },
  additionalClasses: ['text-black', 'dark:text-white'],
};

export const HashedColorThemeChart = {
  light: {
    lightness: [0.4, 0.6],
    saturation: [0.4, 0.8],
    hue: [
      { min: 0, max: 360 },
      { min: 0, max: 360 },
    ],
  },
  dark: {
    lightness: [0.5, 0.7],
    saturation: [0.4, 0.8],
    hue: [
      { min: 0, max: 360 },
      { min: 0, max: 360 },
    ],
  },
};

export const useHashedBackgroundColor = (
  input?: MaybeRef<string | undefined | null>,
  options: UseHashColorOptions = {
    light: { lightness: 0.67, saturation: 0.5 },
    dark: { lightness: 0.3, saturation: 0.5 },
    additionalClasses: ['text-black', 'dark:text-white'],
  },
) => {
  const isDark = usePreferredDark();
  const builderLight = new ColorHash(options.light);
  const builderDark = new ColorHash(options.dark);

  const hashedColorClass = ref<string[]>([
    'bg-[var(--hashed-color-bg)]',
    'dark:bg-[var(--hashed-color-bg-dark)]',
    ...(options.additionalClasses ?? []),
  ]);

  const getHashedColor = (input: string) =>
    isDark.value ? builderDark.hex(input) : builderLight.hex(input);
  const getHashedColorStyle = (input: string) => {
    return {
      '--hashed-color-bg': builderLight.hex(input),
      '--hashed-color-bg-dark': builderDark.hex(input),
    };
  };

  const hashedColor = computed(() => {
    if (!input) return isDark.value ? '#000' : '#fff';

    return getHashedColor(unref(input) ?? '');
  });

  const hashedColorStyle = computed<CSSProperties>(
    () =>
      ({
        '--hashed-color-bg': builderLight.hex(unref(input) ?? ''),
        '--hashed-color-bg-dark': builderDark.hex(unref(input) ?? ''),
      }) as CSSProperties,
  );

  return {
    getHashedColor,
    getHashedColorStyle,
    hashedColorClass,
    hashedColor,
    hashedColorStyle,
  };
};
